import LiquidViewer from "./LiquidViewer";
import type React from "react";

type Props = React.ComponentProps<typeof LiquidViewer>;

const sample = `Hello {{ customer.first_name }}!\n\n{% if cart.items.size > 0 %}\n<ul>\n{% for item in cart.items %}\n  <li>{{ item.name }} — {{ item.price | money }}</li>\n{% endfor %}\n</ul>\n{% else %}\n<p>No items.</p>\n{% endif %}`;

const longTemplate = `\n{% assign total = 0 %}\n{% for item in cart.items %}\n  {% assign total = total | plus: item.price %}\n  - {{ forloop.index }}. {{ item.sku }} — {{ item.price | money }}\n{% endfor %}\n\nSubtotal: {{ total | money }}\n\n{% capture greeting %}Hello {{ customer.first_name }}{% endcapture %}\n{{ greeting }}, your order has {{ cart.items.size }} items.`;

import type { StoryMeta } from "@features/devtools/registry/types";
const meta: StoryMeta<Props> = {
  id: "shared/LiquidViewer",
  title: "LiquidViewer",
  group: "Shared/Code",
  component: LiquidViewer,
  description: "CodeMirror 6 viewer/editor for Liquid with autocomplete.",
  defaultProps: {
    value: sample,
    placeholder: "Type Liquid…",
    readOnly: false,
    autoFocus: false,
    style: { minHeight: 240 },
    enableAutocomplete: true,
    variables: {
      product: { title: "", handle: "", price: 0, vendor: { name: "" } },
      cart: {
        items: [ { id: 1, sku: "SKU-1", name: "", price: 0 } ],
        item_count: 0,
        total_price: 0,
      },
      customer: { first_name: "", last_name: "", email: "" },
      settings: {},
    },
    filters: ["money", "money_with_currency", "date", "upcase", "downcase"],
    tags: ["if", "for", "render", "paginate", "section"],
  } satisfies Partial<Props>,
  controls: {
    value: { type: "code", language: "liquid", label: "Template" },
    enableAutocomplete: { type: "boolean", label: "Autocomplete" },
    readOnly: { type: "boolean", label: "Read only" },
    placeholder: { type: "text", label: "Placeholder" },
    autoFocus: { type: "boolean", label: "Auto focus" },
    style: { type: "json", label: "Style" },
    variables: { type: "json", label: "Variables (object)" },
    filters: { type: "json", label: "Filters[]" },
    tags: { type: "json", label: "Tags[]" },
    schema: { type: "json", label: "Schema (object)" },
  },
  variants: [
    { name: "Editable", props: { readOnly: false } },
    { name: "Read‑only", props: { readOnly: true } },
    { name: "Long template", props: { value: longTemplate } },
    { name: "Autocomplete off", props: { enableAutocomplete: false } },
    { name: "Loop scope", props: { value: `\n{% for item in cart.items %}\n  {{ item. }}\n{% endfor %}` } },
    { name: "Loop scope (dashes)", props: { value: `\n{%- for item in cart.items -%}\n  {{- item. -}}\n{%- endfor -%}` } },
    {
      name: "Flat vars + schema fallback",
      props: {
        variables: ["collection"],
        schema: { "collection.products": ["title", "price", "handle"] },
        value: `\n{% for product in collection.products %}\n  {{ product. }}\n{% endfor %}`,
      },
    },
    { name: "With schema", props: { schema: { product: ["title", "handle", "price", "vendor"], cart: ["items", "item_count", "total_price"], customer: ["first_name", "last_name", "email"] } } },
  ],
};

export default meta;
